import * as def from '@/definitions'
import { Layer } from './layer'
import { CurveLayer } from '@/model/document2/page/surface/design/layer/curveLayer';
import { LineLayer } from '@/model/document2/page/surface/design/layer/lineLayer';
import { PlainTextLayer } from '@/model/document2/page/surface/design/layer/plainTextLayer';
import { RasterImageLayer } from '@/model/document2/page/surface/design/layer/rasterImageLayer';
import { VectorImageLayer } from '@/model/document2/page/surface/design/layer/vectorImageLayer';
import { TableLayer } from '@/model/document2/page/surface/design/layer/tableLayer';
import { RectangleLayer } from './rectangleLayer';
import { CircleLayer } from './circleLayer';
import { PolygonLayer } from './polygonLayer';
import { CircleTextLayer } from './circleTextLayer';
import { BarcodeLayer } from './barcodeLayer';
import { QRcodeLayer } from './qrcodeLayer';
import { D3TextLayer } from './d3TextLayer';
import { DateTextLayer } from './dateTextLayer';
import { NumberLayer } from './numberLayer';
import { Rectangle } from '@/utilityModels/rectangle';
import { Vector2 } from '@/utilityModels/vector2';
import { SpecialPatternLayer } from './specialPatternLayer';
import { CalendarLayer } from './calendarLayer';
import { SimpleTextLayer } from './simpleTextLayer';
import { FileHelper } from '@/helper/fileHelper';

export class GroupLayer extends Layer
{
    public get layerType() : string { return "group"; }

    // グループ位置情報
    private _groupRotate : number = 0;
    public get rotate() { return this._groupRotate; }
    public set rotate(rotate) 
    {
        var delta = rotate - this._groupRotate;

        var centerX = this.centerX;
        var centerY = this.centerY;
        
        for(var i = 0; i < this._layers.length; ++i)
        {
            var layer = this._layers[i];
            var newPos = new Vector2(layer.x, layer.y).rotateDegrees(delta, centerX, centerY);

            layer.rotate += delta;
            layer.x = newPos.x;
            layer.y = newPos.y;
        }

        this._groupRotate = rotate; 
    }

    public get x()
    {
        return this.getVertexes()[0].x;
    }
    
    public set x(x)
    {
        // 移動差分
        var delta = x - this.x;

        // 全レイヤーの移動
        for(var i = 0; i < this._layers.length; ++i)
        {
            this._layers[i].x += delta;
        }
    }

    public get y()
    {
        return this.getVertexes()[0].y;
    }

    public set y(y)
    {
        // 移動差分
        var delta = y - this.y;

        // 全レイヤーの移動
        for(var i = 0; i < this._layers.length; ++i)
        {
            this._layers[i].y += delta;
        }
    }

    public get width()
    {
        var p1 = this.getVertexes()[0];
        var p2 = this.getVertexes()[3];
        return Math.sqrt((p1.x - p2.x) * (p1.x - p2.x) + (p1.y - p2.y) * (p1.y - p2.y));
    }

    public set width(width)
    {
        if(this.width != 0)
        {
            var scale = width / this.width;
            var x = this.x;
            var y = this.y;
            for(var i = 0; i < this._layers.length; ++i)
            {
                var oldX = this._layers[i].x;
                var oldY = this._layers[i].y;
                var dX = oldX - x;
                var dY = oldY - y;
                this._layers[i].width *= scale;
                this._layers[i].height *= scale;
                this._layers[i].x = x + dX * scale;
                this._layers[i].y = y + dY * scale;
            }
        }
    }

    public get height()
    {
        var p1 = this.getVertexes()[0];
        var p2 = this.getVertexes()[1];
        return Math.sqrt((p1.x - p2.x) * (p1.x - p2.x) + (p1.y - p2.y) * (p1.y - p2.y));
    }

    public set height(height)
    {
        if(this.height != 0)
        {
            var scale = height / this.height;
            var x = this.x;
            var y = this.y;
            for(var i = 0; i < this._layers.length; ++i)
            {
                var oldX = this._layers[i].x;
                var oldY = this._layers[i].y;
                var dX = oldX - x;
                var dY = oldY - y;
                this._layers[i].width *= scale;
                this._layers[i].height *= scale;
                this._layers[i].x = x + dX * scale;
                this._layers[i].y = y + dY * scale;
            }
        }
    }

    public get originX()
    {
        var centerX = (this.getVertexes()[0].x + this.getVertexes()[2].x) / 2;
        var centerY = (this.getVertexes()[0].y + this.getVertexes()[2].y) / 2;
        return new Vector2(this.x, this.y).rotateDegrees(-this._groupRotate, centerX, centerY).x;
    }

    public get originY()
    {
        var centerX = (this.getVertexes()[0].x + this.getVertexes()[2].x) / 2;
        var centerY = (this.getVertexes()[0].y + this.getVertexes()[2].y) / 2;
        return new Vector2(this.x, this.y).rotateDegrees(-this._groupRotate, centerX, centerY).y;
    }

    public getVertexes()
    {
        var minX = 0;
        var minY = 0;
        var maxX = 0;
        var maxY = 0;
        for(var i = 0; i < this._layers.length; ++i)
        {
            var vertexes = this._layers[i].getVertexes();
            for(var j = 0; j < vertexes.length; ++j)
            {
                var rotated = vertexes[j].rotateDegrees(-this._groupRotate, 0, 0);
                if(i > 0 || j > 0)
                {
                    minX = Math.min(minX, rotated.x);
                    minY = Math.min(minY, rotated.y);
                    maxX = Math.max(maxX, rotated.x);
                    maxY = Math.max(maxY, rotated.y);
                }
                else
                {
                    minX = rotated.x;
                    minY = rotated.y;
                    maxX = rotated.x;
                    maxY = rotated.y;
                }
            }
        }

        var p1 = new Vector2(minX, minY).rotateDegrees(this._groupRotate, 0, 0);
        var p2 = new Vector2(minX, maxY).rotateDegrees(this._groupRotate, 0, 0);
        var p3 = new Vector2(maxX, maxY).rotateDegrees(this._groupRotate, 0, 0);
        var p4 = new Vector2(maxX, minY).rotateDegrees(this._groupRotate, 0, 0);

        return [ p1, p2, p3, p4 ];
    }

    public getBoundingRect()
    {
        var minX = 0;
        var minY = 0;
        var maxX = 0;
        var maxY = 0;
        for(var i = 0; i < this._layers.length; ++i)
        {
            var bounds = this._layers[i].getBoundingRect();
            minX = i > 0 ? Math.min(minX, bounds.x) : bounds.x;
            minY = i > 0 ? Math.min(minY, bounds.y) : bounds.y;
            maxX = i > 0 ? Math.max(maxX, bounds.x + bounds.width) : bounds.x + bounds.width;
            maxY = i > 0 ? Math.max(maxY, bounds.y + bounds.height) : bounds.y + bounds.height;
        }

        return new Rectangle(minX, minY, maxX - minX, maxY - minY);
    }

    private _layers : Layer[] = new Array();
    public get layers() : Layer[]
    {
        return this._layers;
    }

    public set layers(layers : Layer[]){
        this._layers = layers
    }

    public addLayer(layer : Layer) : void
    {
        this._layers.push(layer);
    }

    public insertLayer(index : number, layer : Layer) : void
    {
        this._layers.splice(index, 0, layer);
    }

    public removeLayer(layer : Layer) : void
    {
        var removeIndex = this.getLayerIndex(layer);

        if(removeIndex >= 0)
        {
            this.removeLayerByIndex(removeIndex);
        }
    }

    public removeLayerByIndex(index : number) : void
    {
        this._layers.splice(index, 1);
    }

    public getLayerCount() : number
    {
        return this._layers.length;
    }

    public getLayer(index : number) : Layer
    {
        return this._layers[index];
    }

    public getLayerIndex(layer : Layer) : number
    {
        var index : number = -1;
        for(var i = 0; i < this._layers.length; ++i)
        {
            if(layer == this._layers[i])
            {
                index = i;
                break;
            }
        }

        return index;
    }

    // すべての子レイヤーを得る。子レイヤーがフォルダに入っていてもその中も得る。
    // isGroupLayerContained : グループレイヤーも一緒に返す
    public getAllChildLayers(isGroupLayerContained) : Layer[]
    {
        return this.getChildLayers(this, isGroupLayerContained);
    }

    // 子レイヤーを再帰で得る
    private getChildLayers(parent : GroupLayer, isGroupLayerContained) : Layer[]
    {
        var layers : Layer[] = [];
        if(isGroupLayerContained)
        {
            layers.push(parent);
        }
        for(var i = 0; i < parent.getLayerCount(); ++i)
        {
            var layer = parent.getLayer(i);
            if (layer instanceof GroupLayer)
            {
                var groupLayer = layer as GroupLayer;
                layers = layers.concat(this.getChildLayers(groupLayer, isGroupLayerContained));
            }
            else 
            {
                layers.push(layer);
            }
        }
        return layers;
    }

    // 開いているか
    private _isExpanded : boolean = false;
    public get isExpanded() { return this._isExpanded; }
    public set isExpanded(isExpanded) { this._isExpanded = isExpanded; }

    public static create() : GroupLayer
    {
        var layer = new GroupLayer();
        return layer;
    }

    // 複製
    public clone()
    {
        var cloned = new GroupLayer();
        this.cloneParameter(cloned);
        return cloned;
    }

    public cloneParameter(layer : GroupLayer)
    {
        layer.isExpanded = this._isExpanded;
        for(var i = 0; i < this._layers.length; ++i)
        {
            layer._layers.push(this._layers[i].clone());
        }
        layer._groupRotate = this._groupRotate;
        
        super.cloneParameter(layer);
    }

    // JSON
    public toJSON()
    {
        var jsonObj = {};
        jsonObj = this.saveParameterToJson(jsonObj);
        return jsonObj;
    }

    public saveParameterToJson(jsonObject)
    {
        jsonObject.isExpanded = this._isExpanded;
        jsonObject.layers = this._layers;
        jsonObject.groupRotate = this._groupRotate;

        jsonObject = super.saveParameterToJson(jsonObject);

        return jsonObject;
    }

    public static createFromJsonData(jsonData : any) : GroupLayer
    {
        var layer = new GroupLayer();
        layer.loadParameterFromJson(jsonData);
        return layer;
    }

    public loadParameterFromJson(jsonData)
    {
        if(jsonData.isExpanded != null) { this._isExpanded = jsonData.isExpanded; }
        if(jsonData.layers != null)
        {
            for(var i = 0; i < jsonData.layers.length; ++i)
            {
                var layer = GroupLayer.createLayerFromJsonData(jsonData.layers[i]);
                this._layers.push(layer);
            } 
        }
        if(jsonData.groupRotate) { this._groupRotate = jsonData.groupRotate; }

        super.loadParameterFromJson(jsonData);
    }

    public static createLayerFromJsonData(jsonData : any)
    {
        if(jsonData.layerType == "group")
        {
            return GroupLayer.createFromJsonData(jsonData);
        }
        else if(jsonData.layerType == "curve")
        {
            return CurveLayer.createFromJsonData(jsonData);
        }
        else if(jsonData.layerType == "line")
        {
            return LineLayer.createFromJsonData(jsonData);
        }
        else if(jsonData.layerType == "rectangle")
        {
            return RectangleLayer.createFromJsonData(jsonData);
        }
        else if(jsonData.layerType == "circle")
        {
            return CircleLayer.createFromJsonData(jsonData);
        }
        else if(jsonData.layerType == "polygon")
        {
            return PolygonLayer.createFromJsonData(jsonData);
        }
        else if(jsonData.layerType == "plainText")
        {
            return PlainTextLayer.createFromJsonData(jsonData);
        }
        else if(jsonData.layerType == "circleText")
        {
            return CircleTextLayer.createFromJsonData(jsonData);
        }
        else if(jsonData.layerType == "d3Text")
        {
            return D3TextLayer.createFromJsonData(jsonData);
        }
        else if(jsonData.layerType == "dateText")
        {
            return DateTextLayer.createFromJsonData(jsonData);
        }
        else if(jsonData.layerType == "number")
        {
            return NumberLayer.createFromJsonData(jsonData);
        }
        else if(jsonData.layerType == "rasterImage")
        {
            return RasterImageLayer.createFromJsonData(jsonData);
        }
        else if(jsonData.layerType == "vectorImage")
        {
            return VectorImageLayer.createFromJsonData(jsonData);
        }
        else if(jsonData.layerType == "table")
        {
            return FileHelper.convertTableLayer(jsonData);
        }
        else if(jsonData.layerType == "barcode")
        {
            return BarcodeLayer.createFromJsonData(jsonData);
        }
        else if(jsonData.layerType == "qrcode")
        {
            return QRcodeLayer.createFromJsonData(jsonData);
        }
        else if(jsonData.layerType == "specialPattern")
        {
            return SpecialPatternLayer.createFromJsonData(jsonData);
        }
        else if(jsonData.layerType == "calendar")
        {
            return CalendarLayer.createFromJsonData(jsonData);
        }
        else if(jsonData.layerType == "simpleText")
        {
            return SimpleTextLayer.createFromJsonData(jsonData);
        }

        return Layer.createFromJsonData(jsonData);

    }
}